// Spotify for Song mode: log in with PKCE (no server, no client secret), keep
// the token fresh, and read what is playing right now.
//
// Setup, once, in the Spotify developer dashboard:
//   • create an app and put its Client ID in REACT_APP_SPOTIFY_CLIENT_ID;
//   • register the redirect URI: this app's origin plus "/". Spotify only
//     accepts HTTPS, or the loopback form http://127.0.0.1:PORT/ for dev — so
//     open the dev build via 127.0.0.1, not localhost;
//   • in development mode, add each Spotify account that may log in.
// Reading playback works on free and Premium accounts alike.

const CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID || ''
const SCOPES = 'user-read-currently-playing user-read-playback-state'
const TOKEN_KEY = 'spotifyToken'   // { access, refresh, expiresAt }
const PKCE_KEY = 'spotifyPkce'     // { verifier, state } — lives only for the round trip
const AUTH = 'https://accounts.spotify.com'
const API = 'https://api.spotify.com/v1'

export const isSpotifyConfigured = () => !!CLIENT_ID
export const redirectUri = () => `${window.location.origin}/`

// PKCE helpers. base64url: the URL-safe alphabet, no padding.
export const b64url = (bytes) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
export const sha256 = async (s) => new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s)))
const randomString = (n) => { const a = new Uint8Array(n); crypto.getRandomValues(a); return b64url(a).slice(0, n) }

const readToken = () => { try { return JSON.parse(localStorage.getItem(TOKEN_KEY) || 'null') } catch { return null } }
const writeToken = (t) => { try { if (t) localStorage.setItem(TOKEN_KEY, JSON.stringify(t)); else localStorage.removeItem(TOKEN_KEY) } catch { /* private mode */ } }
export const isSpotifyConnected = () => !!(readToken() || {}).refresh
export const logoutSpotify = () => writeToken(null)

// Send the browser to Spotify's consent page. The verifier waits in
// sessionStorage for the return trip.
export async function loginWithSpotify() {
  if (!CLIENT_ID) throw new Error('REACT_APP_SPOTIFY_CLIENT_ID is not set.')
  const verifier = randomString(64)
  const state = randomString(16)
  const challenge = b64url(await sha256(verifier))
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }))
  const p = new URLSearchParams({
    client_id: CLIENT_ID, response_type: 'code', redirect_uri: redirectUri(), scope: SCOPES,
    code_challenge_method: 'S256', code_challenge: challenge, state,
  })
  window.location.assign(`${AUTH}/authorize?${p}`)
}

// Call once when the app loads. If the URL carries Spotify's ?code=…&state=…
// from our own login, finish it, clean the URL, and return true so the caller
// can open Song mode. Anything else (no code, someone else's state) → false.
export async function completeSpotifyLogin() {
  const url = new URL(window.location.href)
  const code = url.searchParams.get('code')
  const state = url.searchParams.get('state')
  if (!code || !state) return false
  let pkce = null
  try { pkce = JSON.parse(sessionStorage.getItem(PKCE_KEY) || 'null') } catch { /* ignore */ }
  if (!pkce || pkce.state !== state) return false
  sessionStorage.removeItem(PKCE_KEY)
  url.searchParams.delete('code'); url.searchParams.delete('state')
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  const body = new URLSearchParams({ grant_type: 'authorization_code', code, redirect_uri: redirectUri(), client_id: CLIENT_ID, code_verifier: pkce.verifier })
  const res = await fetch(`${AUTH}/api/token`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body })
  if (!res.ok) throw new Error(`Spotify login failed (${res.status}).`)
  const j = await res.json()
  writeToken({ access: j.access_token, refresh: j.refresh_token, expiresAt: Date.now() + (j.expires_in || 3600) * 1000 })
  return true
}

// A valid access token, refreshed when it has under a minute left. Null when
// not connected (or the refresh was refused, which also forgets the token).
export async function getSpotifyToken() {
  const t = readToken()
  if (!t) return null
  if (t.expiresAt - Date.now() > 60000) return t.access
  if (!t.refresh) return null
  const body = new URLSearchParams({ grant_type: 'refresh_token', refresh_token: t.refresh, client_id: CLIENT_ID })
  const res = await fetch(`${AUTH}/api/token`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body })
  if (!res.ok) { writeToken(null); return null }
  const j = await res.json()
  writeToken({ access: j.access_token, refresh: j.refresh_token || t.refresh, expiresAt: Date.now() + (j.expires_in || 3600) * 1000 })
  return j.access_token
}

// What is playing, normalised to what Song mode needs, or null when nothing
// is. `at` is when we asked, so the position can be extrapolated between
// polls. Errors come back as { error } rather than throwing.
export async function fetchNowPlaying() {
  const token = await getSpotifyToken()
  if (!token) return { error: 'not-connected' }
  let res
  try {
    res = await fetch(`${API}/me/player/currently-playing?additional_types=track`, { headers: { Authorization: `Bearer ${token}` } })
  } catch (e) { return { error: 'network' } }
  if (res.status === 204) return null
  if (res.status === 401) { writeToken(null); return { error: 'not-connected' } }
  if (res.status === 429) return { error: 'rate-limited' }
  if (!res.ok) return { error: `spotify-${res.status}` }
  const j = await res.json()
  const it = j.item
  if (!it || (j.currently_playing_type && j.currently_playing_type !== 'track')) return null
  const images = (it.album && it.album.images) || []
  return {
    id: it.id,
    title: it.name,
    artists: (it.artists || []).map((a) => a.name),
    album: (it.album && it.album.name) || '',
    art: (images[1] && images[1].url) || (images[0] && images[0].url) || '',
    durationMs: it.duration_ms || 0,
    progressMs: j.progress_ms || 0,
    isPlaying: !!j.is_playing,
    at: Date.now(),
  }
}
